import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import './McpTokenManager.css';

const API_URL = process.env.NODE_ENV === 'production' ? '' : 'http://localhost:8000';

interface McpToken {
  token_id: number;
  benutzer_id: number;
  name: string;
  prefix: string;
  aktiv: boolean;
  erstellt_am: string | null;
  laeuft_ab: string | null;
  letzte_nutzung: string | null;
}

interface Props {
  authToken: string;
}

const McpTokenManager: React.FC<Props> = ({ authToken }) => {
  const [tokens, setTokens] = useState<McpToken[]>([]);
  const [loading, setLoading] = useState(true);
  const [neuName, setNeuName] = useState('');
  const [erstelltesToken, setErstelltesToken] = useState<string | null>(null);
  const [kopiert, setKopiert] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);

  const headers = useMemo(() => ({ Authorization: `Bearer ${authToken}` }), [authToken]);

  const loadTokens = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/mcp-tokens`, { headers });
      setTokens(res.data);
    } catch (err) {
      console.error('Fehler beim Laden der MCP-Tokens', err);
      setFehler('MCP-Tokens konnten nicht geladen werden.');
    } finally {
      setLoading(false);
    }
  }, [headers]);

  useEffect(() => { loadTokens(); }, [loadTokens]);

  const handleErstellen = async () => {
    if (!neuName.trim()) return;
    setFehler(null);
    try {
      const res = await axios.post(
        `${API_URL}/api/mcp-tokens`,
        { name: neuName.trim() },
        { headers },
      );
      setErstelltesToken(res.data.token_klartext);
      setKopiert(false);
      setNeuName('');
      loadTokens();
    } catch (err) {
      console.error('Fehler beim Erstellen des Tokens', err);
      setFehler('Token konnte nicht erstellt werden.');
    }
  };

  const handleWiderrufen = async (token: McpToken) => {
    if (!window.confirm(`Token "${token.name}" widerrufen? Verbundene MCP-Clients verlieren sofort den Zugriff.`)) {
      return;
    }
    try {
      await axios.delete(`${API_URL}/api/mcp-tokens/${token.token_id}`, { headers });
      loadTokens();
    } catch (err) {
      console.error('Fehler beim Widerrufen', err);
      setFehler('Token konnte nicht widerrufen werden.');
    }
  };

  const handleKopieren = async () => {
    if (!erstelltesToken) return;
    try {
      await navigator.clipboard.writeText(erstelltesToken);
      setKopiert(true);
    } catch {
      /* Clipboard nicht verfügbar – Nutzer kann manuell kopieren */
    }
  };

  const formatDatum = (iso: string | null) =>
    iso ? new Date(iso).toLocaleString('de-DE', { dateStyle: 'medium', timeStyle: 'short' }) : '–';

  return (
    <div className="mcp-token-container">
      <div className="mcp-token-header">
        <div>
          <h2 className="mcp-token-title">MCP-Zugriff</h2>
          <p className="mcp-token-subtitle">
            Personal Access Tokens für externe MCP-Clients (z.B. Claude Desktop, Claude Code).
            Der Endpoint ist unter <code>/mcp</code> erreichbar; Tools und Berechtigungen
            entsprechen der Rolle des Token-Besitzers.
          </p>
        </div>
      </div>

      {fehler && <div className="mcp-token-fehler">{fehler}</div>}

      {erstelltesToken && (
        <div className="mcp-token-neu-banner">
          <div className="mcp-token-neu-hinweis">
            Token erstellt — jetzt kopieren, es wird <strong>nicht wieder angezeigt</strong>:
          </div>
          <div className="mcp-token-neu-wert">
            <code>{erstelltesToken}</code>
            <button className="btn-secondary" onClick={handleKopieren}>
              {kopiert ? 'Kopiert ✓' : 'Kopieren'}
            </button>
            <button className="btn-secondary" onClick={() => setErstelltesToken(null)}>
              Schließen
            </button>
          </div>
        </div>
      )}

      <div className="mcp-token-create-row">
        <input
          className="mcp-token-name-input"
          placeholder="Token-Name, z.B. 'Claude Desktop Max'"
          value={neuName}
          maxLength={100}
          onChange={e => setNeuName(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') handleErstellen(); }}
        />
        <button className="btn-primary" onClick={handleErstellen} disabled={!neuName.trim()}>
          Token erstellen
        </button>
      </div>

      {loading ? (
        <div className="mcp-token-loading">Tokens werden geladen...</div>
      ) : tokens.length === 0 ? (
        <div className="mcp-token-empty">Noch keine MCP-Tokens vorhanden.</div>
      ) : (
        <table className="mcp-token-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Token</th>
              <th>Status</th>
              <th>Erstellt</th>
              <th>Letzte Nutzung</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {tokens.map(t => (
              <tr key={t.token_id} className={!t.aktiv ? 'row-inaktiv' : ''}>
                <td className="mcp-token-name">{t.name}</td>
                <td><code>{t.prefix}…</code></td>
                <td>
                  <span className={`mcp-token-status ${t.aktiv ? 'aktiv' : 'widerrufen'}`}>
                    {t.aktiv ? 'aktiv' : 'widerrufen'}
                  </span>
                </td>
                <td>{formatDatum(t.erstellt_am)}</td>
                <td>{formatDatum(t.letzte_nutzung)}</td>
                <td>
                  {t.aktiv && (
                    <button className="btn-danger-ghost" onClick={() => handleWiderrufen(t)}>
                      Widerrufen
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
};

export default McpTokenManager;
